import { useEffect, useId, useRef, useState } from 'react';
import { authConfig } from '../../config/auth-config.ts';
import { signedFetch } from '../../auth/signedFetch.ts';
import './UsernameInput.css';

interface Props {
  readonly value: string;
  readonly onChange: (v: string) => void;
  readonly label: string;
  readonly disabled?: boolean;
  readonly required?: boolean;
  /**
   * Callback fired whenever the overall validity (all 4 criteria met)
   * changes. The submit gate in SignUp.tsx uses this so the button can
   * disable while the async uniqueness check is in flight.
   */
  readonly onValidityChange?: (valid: boolean) => void;
  /** The user's CURRENT username (Account edit): typing it back is always
   *  valid — it's theirs — so skip the availability check for that value. */
  readonly ownValue?: string;
}

interface SyncCriterion {
  readonly id: string;
  readonly label: string;
  readonly test: (v: string) => boolean;
}

const SYNC_CRITERIA: readonly SyncCriterion[] = [
  {
    id: 'charset',
    label: 'Letters, digits, and . - _ + = , @',
    test: (v) => v.length > 0 && /^[A-Za-z0-9+=,.\-_@]+$/.test(v),
  },
  {
    id: 'length',
    label: '3 to 32 characters',
    test: (v) => v.length >= 3 && v.length <= 32,
  },
  {
    id: 'no-spaces',
    label: 'No spaces',
    test: (v) => v.length > 0 && !/\s/.test(v),
  },
];

type AsyncState =
  | { kind: 'idle' }
  | { kind: 'checking' }
  | { kind: 'available' }
  | { kind: 'taken' }
  | { kind: 'error'; message: string };

// Wait this long after the last keystroke before hitting the
// availability-check Lambda, so typing a username doesn't fire a request
// per character. Exported so tests advance their fake clock by the same
// amount instead of hard-coding the value.
export const DEBOUNCE_MS = 3000;

export default function UsernameInput({
  value,
  onChange,
  label,
  disabled,
  required,
  onValidityChange,
  ownValue,
}: Props) {
  const id = useId();
  const helpId = useId();
  const [asyncState, setAsyncState] = useState<AsyncState>({ kind: 'idle' });

  // Track the latest in-flight check so a stale response can't overwrite
  // a fresh one. We use an AbortController for the network call and a
  // sequence number for the local state guard (in case AbortController
  // doesn't fire fast enough in some browsers).
  const abortRef = useRef<AbortController | null>(null);
  const seqRef = useRef(0);

  // Re-run sync criteria each render so the checklist below reflects the
  // current `value` instantly. (Async criterion is held in state.)
  const syncResults = SYNC_CRITERIA.map((c) => ({ ...c, met: c.test(value) }));
  const allSyncMet = syncResults.every((r) => r.met);
  const overallValid = allSyncMet && asyncState.kind === 'available';

  // Surface validity to the parent form whenever it changes.
  const lastReportedRef = useRef<boolean | null>(null);
  useEffect(() => {
    if (lastReportedRef.current !== overallValid) {
      lastReportedRef.current = overallValid;
      onValidityChange?.(overallValid);
    }
  }, [overallValid, onValidityChange]);

  // Debounced async check: only run when all sync criteria are met.
  // Otherwise reset to idle.
  useEffect(() => {
    if (abortRef.current !== null) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    if (!allSyncMet) {
      setAsyncState({ kind: 'idle' });
      return;
    }
    // Editing back to the current username — theirs, so trivially available.
    if (ownValue !== undefined && value.toLowerCase() === ownValue.toLowerCase()) {
      setAsyncState({ kind: 'available' });
      return;
    }
    setAsyncState({ kind: 'checking' });
    const mySeq = ++seqRef.current;
    const handle = window.setTimeout(() => {
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      const url = `${authConfig.usernameCheckUrl}?u=${encodeURIComponent(value)}`;
      signedFetch(url, { signal: ctrl.signal })
        .then(async (res) => {
          if (seqRef.current !== mySeq) {
            return;
          }
          if (!res.ok) {
            setAsyncState({ kind: 'error', message: `check failed (${res.status})` });
            return;
          }
          const json = (await res.json()) as { available?: boolean };
          if (seqRef.current !== mySeq) {
            return;
          }
          if (json.available === true) {
            setAsyncState({ kind: 'available' });
          } else if (json.available === false) {
            setAsyncState({ kind: 'taken' });
          } else {
            setAsyncState({ kind: 'error', message: 'unexpected response' });
          }
        })
        .catch((err: unknown) => {
          if (seqRef.current !== mySeq) {
            return;
          }
          // AbortError is expected when the user keeps typing; ignore it.
          if (err instanceof Error && err.name === 'AbortError') {
            return;
          }
          const message = err instanceof Error ? err.message : 'check failed';
          setAsyncState({ kind: 'error', message });
        });
    }, DEBOUNCE_MS);
    return () => {
      window.clearTimeout(handle);
    };
  }, [allSyncMet, value, ownValue]);

  const uniqueLabel =
    asyncState.kind === 'taken'
      ? 'Username is already taken'
      : asyncState.kind === 'error'
        ? `Username check unavailable (${asyncState.message})`
        : 'Username is unique';
  const uniqueMet = asyncState.kind === 'available';
  const uniqueChecking = asyncState.kind === 'checking';
  const uniqueErrored = asyncState.kind === 'error';
  const uniqueRowState: 'met' | 'unmet' | 'checking' | 'error' = uniqueMet
    ? 'met'
    : uniqueChecking
      ? 'checking'
      : uniqueErrored
        ? 'error'
        : 'unmet';
  const uniqueIcon =
    uniqueRowState === 'met'
      ? '✓'
      : uniqueRowState === 'checking'
        ? '⋯'
        : '✕';

  return (
    <div className="om-username">
      <label htmlFor={id} className="om-username__label">
        {label}
      </label>
      <input
        id={id}
        type="text"
        className="om-username__input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete="username"
        required={required}
        disabled={disabled}
        spellCheck={false}
        aria-describedby={helpId}
      />
      <ul
        id={helpId}
        className="om-username__criteria"
        aria-label="Username requirements"
        aria-live="polite"
      >
        {syncResults.map((c) => (
          <li
            key={c.id}
            className="om-username__criterion"
            data-state={c.met ? 'met' : 'unmet'}
            aria-label={`${c.label}: ${c.met ? 'met' : 'not met'}`}
          >
            <span className="om-username__criterion-icon" aria-hidden="true">
              {c.met ? '✓' : '✕'}
            </span>
            <span className="om-username__criterion-label">{c.label}</span>
          </li>
        ))}
        <li
          className="om-username__criterion"
          data-state={uniqueRowState}
          aria-label={`${uniqueLabel}: ${
            uniqueRowState === 'met'
              ? 'met'
              : uniqueRowState === 'checking'
                ? 'checking'
                : 'not met'
          }`}
        >
          <span className="om-username__criterion-icon" aria-hidden="true">
            {uniqueIcon}
          </span>
          <span className="om-username__criterion-label">{uniqueLabel}</span>
        </li>
      </ul>
    </div>
  );
}
